import React, {
  BaseSyntheticEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

import {
  FormControl,
  FormErrorMessage,
  Textarea,
  FormHelperText,
} from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';

import { useErrorMessage } from '../index';
import { useStyles } from '../index';
import { FieldProps, TextAreaFieldSchema, TextAreaFieldStyles } from '../types';

import { FormLabel } from './FormLabel';

export const TextAreaField: FC<FieldProps<TextAreaFieldSchema>> = ({
  id,
  name,
  field,
  isLoading,
}) => {
  const {
    label,
    helpIcon,
    helperText,
    helpLabel,
    isRequired,
    shouldDisplay,
    value,
    styles = {},
    helpTooltip,
    onClickHelpIcon,
    onBlur,
    onChange,
    defaultValue,
    ...rest
  } = field;

  const fieldStyles = useStyles<TextAreaFieldStyles>('textAreaField', styles);

  const { register, watch, setValue } = useFormContext();

  const errorMessage = useErrorMessage(name, label);

  const values = watch();

  const isVisible = useMemo(() => {
    return shouldDisplay ? shouldDisplay(values) : true;
  }, [values, shouldDisplay]);

  const handleOnChange = useCallback(
    (e: BaseSyntheticEvent) => {
      onChange?.(e.target.value, e);
    },
    [onChange],
  );

  useEffect(() => {
    if (value !== undefined && value !== null) {
      setValue(name, value);
    }
  }, [name, setValue, value]);

  // Chain react-hook-form's handlers with the field's own; overriding them
  // meant typed text never reached the form values or validation.
  const textInputProps = useCallback(
    registered => ({
      onChange: e => {
        registered.onChange(e);
        handleOnChange(e);
      },
      onBlur: e => {
        registered.onBlur(e);
        onBlur?.(e.target.value);
      },
      defaultValue,
    }),
    [onBlur, handleOnChange, defaultValue],
  );
  const registered = register(name);

  return isVisible ? (
    <FormControl
      key={`${name}-control`}
      isRequired={isRequired}
      isInvalid={!!errorMessage}
      {...fieldStyles.control}
    >
      {!!label && (
        <FormLabel
          htmlFor={name}
          label={label}
          helpIcon={helpIcon}
          styles={fieldStyles.label}
          helpLabel={helpLabel}
          helpTooltip={helpTooltip}
          isRequired={isRequired}
          onClickHelpIcon={onClickHelpIcon}
        />
      )}
      <Textarea
        id={id}
        data-testid={id}
        {...rest}
        {...registered}
        {...fieldStyles.textArea}
        {...textInputProps(registered)}
      />
      {!!helperText && (
        <FormHelperText {...fieldStyles.helperText}>
          {helperText}
        </FormHelperText>
      )}
      <FormErrorMessage {...fieldStyles.errorMessage}>
        {errorMessage}
      </FormErrorMessage>
    </FormControl>
  ) : null;
};
